import React, { useEffect } from "react";
import {
  LegalContainer,
  LegalInner,
  HeaderTitle,
  Subtle,
  BoxDiv,
  SectionTitle,
  SubTitle,
  Table,
  CrossLinks,
  PageLink,
} from "./legalStyles";

const LAST_UPDATED = "26 May 2026";
const CONTACT_EMAIL = "nassos@duck.com";

const CookiePolicyPage = () => {
  useEffect(() => {
    document.title = "NΛSOS — Cookie Policy";
  }, []);

  return (
    <LegalContainer>
      <LegalInner>
        <HeaderTitle>Cookie Policy</HeaderTitle>
        <Subtle>Last updated: {LAST_UPDATED}</Subtle>
        <CrossLinks>
          <PageLink to="/privacy">← Privacy Policy</PageLink>
        </CrossLinks>

        <BoxDiv>
          <p>
            <strong>Short version:</strong> n450s.com uses <strong>no analytics, advertising, or
            tracking cookies</strong>. The only things we store on your device are strictly
            necessary to run the Site (for example, to keep you signed in) or to remember your
            own preferences. Because we set no non-essential cookies, EU/UK law does not require
            a cookie consent banner — so you won&apos;t see one here.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>What are cookies and local storage?</SectionTitle>
          <p>
            A <strong>cookie</strong> is a small file a website stores in your browser.{" "}
            <strong>Local storage</strong> and <strong>session storage</strong> are similar
            browser mechanisms for keeping small pieces of data on your device. They can be{" "}
            <em>strictly necessary</em> (the site cannot work without them) or{" "}
            <em>functional</em> (they remember your choices). We use only these categories — never
            advertising or analytics/tracking technologies.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>Cookies we use</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>Cookie</th>
                <th>Set by</th>
                <th>Purpose</th>
                <th>Category</th>
                <th>Lifetime</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Session / authentication cookie (HttpOnly)</td>
                <td>n450s.com</td>
                <td>Keeps you signed in and securely refreshes your login.</td>
                <td>Strictly necessary</td>
                <td>Until you sign out or it expires</td>
              </tr>
              <tr>
                <td>
                  <code>_GRECAPTCHA</code>
                </td>
                <td>Google reCAPTCHA</td>
                <td>
                  Distinguishes humans from bots on submission forms (e.g. coffee ratings,
                  comments). Set by Google only when a protected form is shown.
                </td>
                <td>Strictly necessary (security)</td>
                <td>Set and managed by Google</td>
              </tr>
            </tbody>
          </Table>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>Browser storage we use</SectionTitle>
          <p>
            These values stay in your browser and are never sent anywhere for tracking.
          </p>

          <SubTitle>Session storage (cleared when you close the tab)</SubTitle>
          <Table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Purpose</th>
                <th>Category</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>OAuth state, PKCE verifier &amp; nonce</td>
                <td>Protect the sign-in (OAuth) flow from tampering.</td>
                <td>Strictly necessary</td>
              </tr>
              <tr>
                <td>Silent-auth flag</td>
                <td>Coordinates a background sign-in attempt.</td>
                <td>Functional</td>
              </tr>
            </tbody>
          </Table>

          <SubTitle>Local storage (kept until you clear it)</SubTitle>
          <Table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Purpose</th>
                <th>Category</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Chat session token</td>
                <td>Keeps you connected to chat.</td>
                <td>Functional</td>
              </tr>
              <tr>
                <td>Coffee rating draft</td>
                <td>Saves an unfinished coffee rating so you don&apos;t lose it.</td>
                <td>Functional</td>
              </tr>
              <tr>
                <td>Last map location (coffee shops)</td>
                <td>Remembers where you were last looking on the map.</td>
                <td>Functional</td>
              </tr>
              <tr>
                <td>Layout &amp; UI preferences</td>
                <td>Remember choices such as legacy vs. modern layout and expanded panels.</td>
                <td>Functional</td>
              </tr>
              <tr>
                <td>Chat "last seen" timestamps</td>
                <td>Track which messages you&apos;ve already read.</td>
                <td>Functional</td>
              </tr>
              <tr>
                <td>Install / prompt dismissals</td>
                <td>Remember that you dismissed the "install app" or pending prompts.</td>
                <td>Functional</td>
              </tr>
              <tr>
                <td>Icon &amp; response caches</td>
                <td>Speed up the Site by caching icons and data locally.</td>
                <td>Functional (performance)</td>
              </tr>
            </tbody>
          </Table>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>Third-party services loaded on demand</SectionTitle>
          <p>
            A couple of Google services load only when a specific feature needs them, and they
            may set their own cookies under Google&apos;s terms:
          </p>
          <ul>
            <li>
              <strong>Google reCAPTCHA</strong> — loaded on forms that need bot protection.
            </li>
            <li>
              <strong>Google Maps</strong> — loaded only when you use coffee-shop location
              features.
            </li>
          </ul>
          <p>
            Fonts are <strong>self-hosted</strong> on our own servers, so simply visiting the
            Site does not send your IP address to any font CDN.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>How to control cookies and storage</SectionTitle>
          <p>
            You can delete or block cookies and clear site storage at any time through your
            browser settings. Note that blocking the strictly-necessary items will stop you
            from signing in or using parts of the Site. Functional items can be cleared without
            losing access — you may just lose saved preferences or drafts.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>Changes &amp; contact</SectionTitle>
          <p>
            We may update this Cookie Policy as the Site evolves; the "Last updated" date above
            will change accordingly. For anything cookie- or privacy-related, email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. See also our{" "}
            <PageLink to="/privacy">Privacy Policy</PageLink>.
          </p>
        </BoxDiv>

        <CrossLinks>
          <PageLink to="/privacy">← Privacy Policy</PageLink>
        </CrossLinks>
      </LegalInner>
    </LegalContainer>
  );
};

export default CookiePolicyPage;
